import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import apiClient from '../utils/apiClient.js';
import { getApiErrorMessage } from '../utils/apiError';

const fetchDailyReport = async () => {
  try {
    const response = await apiClient.get('/Reports/Daily', {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching daily report:', error.message);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch daily report'
    );
  }
};
const processData = data => {
  const columns = new Set(['time']);
  const rows = data
    .map(item => {
      const { time, ...rest } = item;
      const date = new Date(time);
      const formattedTime = date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      const row = { time: formattedTime, ...rest };
      Object.keys(rest).forEach(key => columns.add(key));
      return row;
    })
    .sort((a, b) => new Date(b.time) - new Date(a.time)); // Sort descending

  return {
    columns: Array.from(columns),
    rows,
  };
};

const DailyReportTable = () => {
  const { data, error, isLoading } = useQuery('dailyReport', fetchDailyReport, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { columns, rows } = useMemo(
    () => (data ? processData(data) : { columns: [], rows: [] }),
    [data]
  );

  if (isLoading) return <Text>Loading...</Text>;
  if (error) return <Text>An error occurred: {getApiErrorMessage(error)}</Text>;

  return (
    <Box w="100%">
      <Heading size="md" mb={5}>
        Daily Report
      </Heading>
      <TableContainer height="90vh" overflowY="auto">
        <Table variant="simple" colorScheme="gray">
          <Thead>
            <Tr>
              {columns.map((column, idx) => (
                <Th key={idx} px={4} py={2}>
                  {column}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                {columns.map((column, colIndex) => (
                  <Td key={colIndex} px={4} py={2}>
                    {row[column] || '-'}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DailyReportTable;
